import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { Typography } from "@material-ui/core";

const NODE_W = 220;
const NODE_MIN_H = 48;
const GAP_MIN = 32;
const ARROW_LEAD = 24;
const ARROW_TAIL = 24;
const PAD = 20;
const CANVAS_MIN_W = 280;
const LABEL_LINE_H = 14;
const LABEL_PAD = 8;

const OUTCOME_COLORS = {
  store: { bg: "#1565c0", border: "#42a5f5", text: "#e3f2fd" },
  endCard: { bg: "#2e7d32", border: "#66bb6a", text: "#e8f5e9" },
  combined: { bg: "#6a1b9a", border: "#ab47bc", text: "#f3e5f5" },
};

const TYPE_COLORS = {
  start: { bg: "#424242", border: "#757575", text: "#f5f5f5" },
  milestone: { bg: "#37474f", border: "#78909c", text: "#eceff1" },
  note: { bg: "#333333", border: "#616161", text: "#bdbdbd" },
};

function buildAdjacency(edges) {
  const adj = new Map();
  (edges || []).forEach(({ from, to }) => {
    if (!adj.has(from)) adj.set(from, []);
    adj.get(from).push(to);
  });
  return adj;
}

function edgeKey(from, to, label) {
  return `${from}-${to}-${label || ""}`;
}

/** BFS depth + stable vertical order. */
function orderNodes(gameFlow) {
  const nodes = gameFlow?.nodes || [];
  const edges = gameFlow?.edges || [];
  const start = nodes.find((n) => n.type === "start");
  if (!start) return [];

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const adj = buildAdjacency(edges);
  const depth = new Map([[start.id, 0]]);
  const queue = [start.id];

  while (queue.length > 0) {
    const id = queue.shift();
    const d = depth.get(id);
    (adj.get(id) || []).forEach((next) => {
      if (!depth.has(next)) {
        depth.set(next, d + 1);
        queue.push(next);
      }
    });
  }

  let maxDepth = Math.max(0, ...depth.values());
  nodes.forEach((n) => {
    if (!depth.has(n.id)) {
      maxDepth += 1;
      depth.set(n.id, maxDepth);
    }
  });

  return [...nodes]
    .sort((a, b) => {
      const da = depth.get(a.id) ?? 0;
      const db = depth.get(b.id) ?? 0;
      if (da !== db) return da - db;
      return a.id.localeCompare(b.id);
    })
    .map((n) => byId.get(n.id));
}

function findEdgeBetween(edges, fromId, toId) {
  return (edges || []).find((e) => e.from === fromId && e.to === toId) || null;
}

function estimateLabelHeight(label, maxWidth) {
  if (!label) return 0;
  const charsPerLine = Math.max(16, Math.floor(maxWidth / 6.5));
  const lines = Math.ceil(label.length / charsPerLine);
  return lines * LABEL_LINE_H + LABEL_PAD + 6;
}

function heightsEqual(a, b) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => a[k] === b[k]);
}

function computeLayout(gameFlow, measuredHeights, canvasWidth) {
  const ordered = orderNodes(gameFlow);
  if (ordered.length === 0) {
    return { nodes: [], edges: [], width: CANVAS_MIN_W, height: 0 };
  }

  const width = Math.max(CANVAS_MIN_W, canvasWidth || CANVAS_MIN_W);
  const nodeX = (width - NODE_W) / 2;
  const edges = gameFlow.edges || [];

  let y = PAD;
  const laidOut = [];

  ordered.forEach((node, index) => {
    const h = measuredHeights[node.id] || NODE_MIN_H;
    laidOut.push({ node, x: nodeX, y, w: NODE_W, h });
    y += h;

    if (index < ordered.length - 1) {
      const next = ordered[index + 1];
      const edge = findEdgeBetween(edges, node.id, next.id);
      const labelH = edge?.label ? estimateLabelHeight(edge.label, NODE_W + 24) : 0;
      if (edge?.label) {
        y += ARROW_LEAD + labelH + ARROW_TAIL;
      } else {
        y += GAP_MIN;
      }
    }
  });

  const byId = new Map(laidOut.map((item) => [item.node.id, item]));
  const edgeGraphics = [];

  ordered.forEach((node, index) => {
    if (index >= ordered.length - 1) return;
    const next = ordered[index + 1];
    const from = byId.get(node.id);
    const to = byId.get(next.id);
    const edge = findEdgeBetween(edges, node.id, next.id);
    if (!from || !to) return;

    const cx = from.x + from.w / 2;
    const gapStart = from.y + from.h;
    const gapEnd = to.y;

    const key = edge ? edgeKey(edge.from, edge.to, edge.label) : `${node.id}-${next.id}`;
    const labelH = edge?.label ? estimateLabelHeight(edge.label, NODE_W + 24) : 0;
    const labelTop = edge?.label ? gapStart + ARROW_LEAD : 0;

    edgeGraphics.push({
      key,
      cx,
      gapStart,
      gapEnd,
      label: edge?.label || null,
      labelTop,
      labelH,
      labelW: NODE_W + 24,
      line1End: edge?.label ? labelTop : gapEnd,
      line2Start: edge?.label ? labelTop + labelH : null,
    });
  });

  // Any edges not between consecutive ordered nodes (branches)
  edges.forEach((edge) => {
    const from = byId.get(edge.from);
    const to = byId.get(edge.to);
    if (!from || !to) return;
    const key = edgeKey(edge.from, edge.to, edge.label);
    if (edgeGraphics.some((g) => g.key === key)) return;

    const cx = from.x + from.w / 2;
    const gapStart = from.y + from.h;
    const gapEnd = to.y;
    const labelH = edge.label ? estimateLabelHeight(edge.label, NODE_W + 24) : 0;
    const labelTop = edge.label ? gapStart + ARROW_LEAD : 0;
    const gapEndResolved = edge.label ? gapStart + ARROW_LEAD + labelH + ARROW_TAIL : gapStart + GAP_MIN;

    edgeGraphics.push({
      key,
      cx,
      gapStart,
      gapEnd: gapEndResolved,
      label: edge.label || null,
      labelTop,
      labelH,
      labelW: NODE_W + 24,
      line1End: edge.label ? labelTop : gapEndResolved,
      line2Start: edge.label ? labelTop + labelH : null,
    });
  });

  const totalHeight = y + PAD;
  return { nodes: laidOut, edges: edgeGraphics, width, height: totalHeight };
}

function NodeBox({ item, innerRef }) {
  const { node, x, y, w } = item;
  const colors =
    node.type === "outcome"
      ? OUTCOME_COLORS[node.outcome] || OUTCOME_COLORS.store
      : TYPE_COLORS[node.type] || TYPE_COLORS.note;

  const detailLine =
    node.type === "milestone" && node.trigger?.filterSummary
      ? node.trigger.filterSummary
      : null;

  return (
    <div
      ref={innerRef}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 8,
        border: `2px solid ${colors.border}`,
        backgroundColor: colors.bg,
        color: colors.text,
        zIndex: 2,
        textAlign: "center",
      }}
    >
      <Typography
        variant="caption"
        style={{ display: "block", fontWeight: 700, lineHeight: 1.3, fontSize: 12, textAlign: "center" }}
      >
        {node.label}
      </Typography>
      {detailLine && (
        <Typography
          variant="caption"
          style={{
            display: "block",
            marginTop: 6,
            fontSize: 10,
            lineHeight: 1.35,
            opacity: 0.88,
            textAlign: "center",
          }}
        >
          {detailLine}
        </Typography>
      )}
    </div>
  );
}

/**
 * Read-only branching diagram for dashboardMeta.gameFlow.
 */
export default function GameFlowDiagram({ gameFlow }) {
  const containerRef = useRef(null);
  const nodeRefs = useRef({});
  const [canvasWidth, setCanvasWidth] = useState(CANVAS_MIN_W);
  const [measuredHeights, setMeasuredHeights] = useState({});

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const updateWidth = () => {
      const w = el.clientWidth;
      if (w > 0) setCanvasWidth((prev) => {
        const next = Math.max(CANVAS_MIN_W, w);
        return prev === next ? prev : next;
      });
    };
    updateWidth();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(updateWidth);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const layout = useMemo(
    () => computeLayout(gameFlow, measuredHeights, canvasWidth),
    [gameFlow, measuredHeights, canvasWidth]
  );

  // Measure node heights once after paint — no label measurement (avoids layout feedback loops).
  useLayoutEffect(() => {
    if (!gameFlow?.nodes?.length) {
      setMeasuredHeights((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return undefined;
    }

    let cancelled = false;
    let frames = 0;

    const measure = () => {
      if (cancelled || frames > 8) return;
      frames += 1;

      const heights = {};
      let ready = true;
      gameFlow.nodes.forEach((n) => {
        const el = nodeRefs.current[n.id];
        if (!el) {
          ready = false;
          return;
        }
        heights[n.id] = el.offsetHeight;
      });

      if (!ready) {
        requestAnimationFrame(measure);
        return;
      }

      setMeasuredHeights((prev) => (heightsEqual(prev, heights) ? prev : heights));
    };

    requestAnimationFrame(measure);
    return () => {
      cancelled = true;
    };
  }, [gameFlow, canvasWidth]);

  if (!gameFlow || !Array.isArray(gameFlow.nodes) || gameFlow.nodes.length === 0) {
    return (
      <Typography variant="body2" style={{ color: "#aaa", fontStyle: "italic" }}>
        No game flow defined yet. Ask the agent to describe end-card and store paths.
      </Typography>
    );
  }

  const { nodes, edges, width, height } = layout;

  return (
    <div>
      {gameFlow.summary && (
        <Typography variant="body2" style={{ color: "#ccc", marginBottom: 12 }}>
          {gameFlow.summary}
        </Typography>
      )}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "auto",
          maxHeight: 420,
          borderRadius: 6,
          backgroundColor: "#2a2a2a",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ position: "relative", width, height, minWidth: width }}>
          <svg
            width={width}
            height={height}
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 0 }}
          >
            <defs>
              <marker
                id="gameflow-arrowhead"
                markerWidth="10"
                markerHeight="8"
                refX="9"
                refY="4"
                orient="auto"
              >
                <polygon points="0 0, 10 4, 0 8" fill="#aaa" />
              </marker>
            </defs>
            {edges.map((e) => (
              <g key={`lines-${e.key}`}>
                {e.label ? (
                  <>
                    <line
                      x1={e.cx}
                      y1={e.gapStart}
                      x2={e.cx}
                      y2={e.line1End}
                      stroke="#aaa"
                      strokeWidth={2}
                    />
                    <line
                      x1={e.cx}
                      y1={e.line2Start}
                      x2={e.cx}
                      y2={e.gapEnd}
                      stroke="#aaa"
                      strokeWidth={2}
                      markerEnd="url(#gameflow-arrowhead)"
                    />
                  </>
                ) : (
                  <line
                    x1={e.cx}
                    y1={e.gapStart}
                    x2={e.cx}
                    y2={e.gapEnd}
                    stroke="#aaa"
                    strokeWidth={2}
                    markerEnd="url(#gameflow-arrowhead)"
                  />
                )}
              </g>
            ))}
          </svg>

          {edges.map((e) =>
            e.label ? (
              <div
                key={`label-${e.key}`}
                style={{
                  position: "absolute",
                  left: e.cx,
                  top: e.labelTop,
                  transform: "translateX(-50%)",
                  width: e.labelW,
                  minHeight: e.labelH,
                  boxSizing: "border-box",
                  padding: "4px 8px",
                  textAlign: "center",
                  fontSize: 10,
                  lineHeight: `${LABEL_LINE_H}px`,
                  color: "#ccc",
                  backgroundColor: "#2a2a2a",
                  borderRadius: 4,
                  zIndex: 1,
                  pointerEvents: "none",
                  wordBreak: "break-word",
                }}
              >
                {e.label}
              </div>
            ) : null
          )}

          {nodes.map((item) => (
            <NodeBox
              key={item.node.id}
              item={item}
              innerRef={(el) => {
                nodeRefs.current[item.node.id] = el;
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
